import React from 'react';
import { Receipt, CheckCircle2, Clock, AlertTriangle, Percent, Wallet, Timer, TrendingUp, ChevronRight } from 'lucide-react';
import type { Kpis } from '../types';
import type { IdKpi } from '../../../../src/lib/financeiro/definicoesKpi';

interface Props {
  kpis: Kpis;
  variacaoVencidoPct: number | null; // vem do comparativo mensal (calcularComparativos)
  rotuloPeriodo: string; // ex.: "Últimos 12 meses" — o Vencido respeita o filtro
  /** Abre a lista de boletos que forma o KPI. Omitido, os cards não são clicáveis
   *  (é o caso do Painel Executivo geral, que reaproveita só os números). */
  onAbrir?: (id: IdKpi) => void;
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtPct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const fmtDias = (v: number | null) => (v === null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`);

/**
 * A pontualidade em palavras. O sinal sozinho engana: "−3,1 dias" parece erro,
 * e o que ele diz é que o cliente paga 3 dias ANTES de vencer.
 *
 * Com boleto ainda por vencer no recorte, o texto avisa: os atrasos do fim do
 * período não aconteceram e o número ainda vai piorar.
 */
function rotuloPontualidade(desvio: number | null, aindaMaturando: boolean): string | undefined {
  if (desvio === null) return undefined;
  const dias = Math.abs(desvio).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  const base =
    Math.abs(desvio) < 0.05 ? 'Paga no dia do vencimento'
      : desvio < 0 ? `Paga ${dias} dias antes de vencer`
        : `Paga ${dias} dias depois de vencer`;
  return aindaMaturando ? `${base} · período em curso` : base;
}

function KpiCard({
  icon: Icon, tint, titulo, valor, subtexto, badge, badgeTint, onClick,
}: {
  icon: React.ElementType; tint: string; titulo: string; valor: string; subtexto?: string; badge?: string; badgeTint?: string; onClick?: () => void;
}) {
  const conteudo = (
    <>
      <div className={`p-2.5 rounded-xl shrink-0 ${tint}`}><Icon size={18} /></div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{titulo}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 truncate">{valor}</p>
        {subtexto && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{subtexto}</p>}
        {badge && <p className={`text-[11px] font-semibold mt-0.5 truncate ${badgeTint || 'text-gray-500 dark:text-gray-400'}`}>{badge}</p>}
        {onClick && (
          <p className="text-[11px] font-semibold text-primary mt-1 inline-flex items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
            Ver boletos <ChevronRight size={12} />
          </p>
        )}
      </div>
    </>
  );
  const base = 'bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-start gap-3';
  if (!onClick) return <div className={base}>{conteudo}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${titulo}: ${valor}. Ver os boletos`}
      className={`${base} group text-left w-full hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-all`}
    >
      {conteudo}
    </button>
  );
}

const KpiCards: React.FC<Props> = ({ kpis, variacaoVencidoPct, rotuloPeriodo, onAbrir }) => {
  const abrir = (id: IdKpi) => (onAbrir ? () => onAbrir(id) : undefined);
  const nivelTaxa = kpis.taxaInadimplencia > 0.15 ? 'alto' : kpis.taxaInadimplencia >= 0.08 ? 'atencao' : 'controle';
  const taxaTint =
    nivelTaxa === 'alto' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
      : nivelTaxa === 'atencao' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
        : 'bg-primary/10 text-primary';
  const taxaBadgeTint =
    nivelTaxa === 'alto' ? 'text-red-600 dark:text-red-400'
      : nivelTaxa === 'atencao' ? 'text-amber-600 dark:text-amber-400'
        : 'text-primary';
  const rotuloTaxa = nivelTaxa === 'alto' ? 'Nível alto' : nivelTaxa === 'atencao' ? 'Atenção' : 'Sob controle';

  // Uma coluna no celular: com dois por linha, "R$ 226.925,54" e o título do
  // card vinham truncados ("FATURAMENTO …"), que é o que o card existe para mostrar.
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard
        icon={Receipt} tint="bg-primary/10 text-primary"
        titulo="Faturamento Emitido" valor={fmtBRL(kpis.emitido)} onClick={abrir('emitido')}
        subtexto={`${kpis.qtdeBoletos.toLocaleString('pt-BR')} boletos`}
      />
      <KpiCard
        icon={CheckCircle2} tint="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
        titulo="Recebido" valor={fmtBRL(kpis.recebido)} onClick={abrir('recebido')}
        badge={`${fmtPct(kpis.recebidoPctEmitido)} do emitido`} badgeTint="text-emerald-600 dark:text-emerald-400"
      />
      <KpiCard
        icon={Clock} tint="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400"
        titulo="A Vencer" valor={fmtBRL(kpis.emAberto)} onClick={abrir('aVencer')}
        subtexto={`${fmtBRL(kpis.emAbertoValorVencendo7dias)} (${kpis.emAbertoQtdeVencendo7dias}) vencendo em 7 dias`}
      />
      <KpiCard
        icon={AlertTriangle} tint="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
        titulo="Vencido (Inadimplência)" valor={fmtBRL(kpis.vencido)} onClick={abrir('vencido')}
        // O período fica explícito porque este é o número que se compara com a
        // Central Executiva de Inadimplência, que não recorta período por padrão.
        subtexto={`${kpis.vencidoQtdeBoletos} boletos · ${kpis.vencidoClientesDistintos} clientes · ${rotuloPeriodo}`}
        badge={variacaoVencidoPct !== null ? `${variacaoVencidoPct >= 0 ? '+' : ''}${variacaoVencidoPct.toFixed(1)}% vs 30 dias atrás` : undefined}
        // A cor acompanha o sinal, e não é mais vermelho fixo: com a regra antiga
        // (vencido do mês × mês anterior) a conta dava queda TODO mês, então o
        // vermelho cravado nunca destoava. Agora que o número pode subir ou cair
        // de verdade, pintar de vermelho uma queda de inadimplência diria o
        // oposto do que aconteceu. Para dívida, cair é bom — inverso do
        // faturamento (mesma semântica de `corSemantica` em calcularComparativos).
        badgeTint={variacaoVencidoPct !== null && variacaoVencidoPct < 0
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-red-600 dark:text-red-400'}
      />
      <KpiCard
        icon={Percent} tint={taxaTint}
        titulo="Taxa de Inadimplência" valor={fmtPct(kpis.taxaInadimplencia)} onClick={abrir('taxaInadimplencia')}
        badge={rotuloTaxa} badgeTint={taxaBadgeTint}
      />
      <KpiCard icon={Wallet} tint="bg-primary/10 text-primary" titulo="Ticket Médio" valor={fmtBRL(kpis.ticketMedio)} onClick={abrir('ticketMedio')} />
      {/* O valor é EMISSÃO → PAGAMENTO. A pontualidade (pagamento − vencimento)
          vai no subtexto, em palavras: ela é negativa na maior parte do tempo
          porque a maioria paga adiantado, e "prazo de −3,1 dias" não é prazo. */}
      <KpiCard
        icon={Timer} tint="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
        titulo="Prazo Médio de Recebimento" valor={fmtDias(kpis.prazoMedioRecebimentoDias)} onClick={abrir('prazoMedio')}
        subtexto={`Da emissão ao pagamento · mediana ${fmtDias(kpis.prazoMedianoRecebimentoDias)}`}
        badge={rotuloPontualidade(kpis.desvioMedioVencimentoDias, kpis.prazoAindaMaturando)}
        badgeTint={
          kpis.desvioMedioVencimentoDias === null || kpis.desvioMedioVencimentoDias <= 0
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-amber-600 dark:text-amber-400'
        }
      />
      <KpiCard
        icon={TrendingUp} tint="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
        titulo="Recuperado" valor={fmtBRL(kpis.recuperado)} onClick={abrir('recuperado')}
        subtexto={`${kpis.recuperadoQtdePagosComAtraso} boletos pagos com atraso`}
      />
    </div>
  );
};

export default KpiCards;
